/**
 * Drawdown Over Time — shadcn interactive line chart.
 */

import React, { useId, useMemo, useState } from "react";
import { Area, CartesianGrid, ComposedChart, XAxis, YAxis } from "recharts";
import { Box, Skeleton, Text, useColorModeValue } from "@chakra-ui/react";
import { useTheme } from "next-themes";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { cn } from "@/lib/utils";
import {
  STATS_CHART_TOOLTIP_TITLE_CLASS,
  STATISTICS_NO_DATA_TIMEFRAME_MESSAGE,
  StatisticsChartTooltipRow,
  statsChartTooltipContentClassName,
} from "./statisticsChartTooltip";
import type { RiskMetricsResponse } from "../../../../types/trade";
import { useDisplayTimeZone } from "../../../../contexts/DisplayTimezoneContext";
import { useDisplayCurrency } from "../../../../contexts/DisplayCurrencyContext";
import { formatDisplayCurrency } from "../../../../utils/formatDisplayCurrency";
import {
  formatChartHourAxisLabel,
  formatTradeDateOnlyForDisplay,
  formatYmdBucketShortInZone,
  parseStatisticsChartInstant,
  shouldIncludeYearOnChartAxis,
} from "../../../../utils/displayDateTime";
import {
  hourlyChartSpansMultipleDays,
  resolveChartTimeGrain,
} from "../../../../utils/chartDataSampling";
import { useStatisticsWidgetErrorGate } from "../../../../hooks/useStatisticsWidgetErrorGate";
import { StatisticsInlineError } from "../../../../components/errors/StatisticsInlineError";
import {
  StatisticsCardHeadline,
  STATS_FONT_FAMILY,
  useStatsCardHeadlineColor,
} from "./statisticsCardHeadline";
import {
  statisticsShadcnBarChartAreaClassName,
  statisticsShadcnCardClassName,
  statisticsShadcnCardContentClassName,
  statisticsShadcnDailyActivityChartContainerClassName,
} from "./statisticsShadcnCardShell";

const DRAWDOWN_LINE_COLOR = "#F70009";
const DRAWDOWN_FILL_SOFT = "#FF6B70";

type DrawdownSeriesKey = "drawdown" | "drawdownPercent";

const chartConfig = {
  views: { label: "Drawdown" },
  drawdown: {
    label: "Maximum Drawdown",
    color: DRAWDOWN_LINE_COLOR,
  },
  drawdownPercent: {
    label: "Max Drawdown %",
    color: DRAWDOWN_LINE_COLOR,
  },
} satisfies ChartConfig;

type DrawdownChartDatum = {
  date: string;
  label: string;
  drawdown: number;
  drawdownPercent: number;
};

function formatPercentValue(value: number | null): string {
  if (value === null || Number.isNaN(value)) {
    return "N/A";
  }
  return `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;
}

function drawdownPercentForPoint(drawdown: number, portfolioValue: number): number {
  if (!Number.isFinite(portfolioValue) || portfolioValue <= 0) {
    return 0;
  }
  return (Math.abs(drawdown) / portfolioValue) * 100;
}

export interface DrawdownOverTimeChartProps {
  drawdownData?: RiskMetricsResponse["drawdown_data"];
  maxDrawdown?: number | null;
  maxDrawdownPercent?: number | null;
  loading?: boolean;
  error?: string | null;
  errorAction?: string | null;
  /** Chart only — no header or card chrome. */
  compact?: boolean;
  /** Toggle header + chart without outer Card (e.g. comparison period boxes). */
  embedded?: boolean;
  /** Stretch card and chart to fill a matched-height row (Statistics pair / dashboard). */
  fillHeight?: boolean;
  title?: string;
  description?: string;
}

const DrawdownOverTimeChart: React.FC<DrawdownOverTimeChartProps> = ({
  drawdownData = [],
  maxDrawdown = null,
  maxDrawdownPercent = null,
  loading = false,
  error = null,
  errorAction = null,
  compact = false,
  embedded = false,
  fillHeight = false,
  title = "Drawdown Over Time",
  description = "Peak-to-trough decline over the selected period",
}) => {
  const { resolvedTheme } = useTheme();
  const displayTimeZone = useDisplayTimeZone();
  const { displayCurrency } = useDisplayCurrency();
  const axisTickColor = useStatsCardHeadlineColor();
  const errorColor = useColorModeValue("red.500", "red.300");
  const themeClass = resolvedTheme === "dark" ? "dark" : "";
  const [activeSeries, setActiveSeries] = useState<DrawdownSeriesKey>("drawdown");

  const chartData = useMemo((): DrawdownChartDatum[] => {
    if (!drawdownData?.length) {
      return [];
    }

    const pointDates = drawdownData.map((point) => point.date);
    const timeGrain = resolveChartTimeGrain({
      dates: pointDates,
    });
    const isHourly = timeGrain === "hourly";
    const hourlyIncludeDay = isHourly && hourlyChartSpansMultipleDays(pointDates);

    const ymdBuckets = drawdownData
      .map((point) => (point.date.length >= 10 ? point.date.slice(0, 10) : point.date))
      .filter(Boolean);
    const includeYear = !isHourly && shouldIncludeYearOnChartAxis(ymdBuckets);

    return drawdownData.map((point) => {
      let label: string;
      if (isHourly) {
        const parsed = parseStatisticsChartInstant(point.date);
        label = parsed
          ? formatChartHourAxisLabel(parsed, displayTimeZone, { includeDay: hourlyIncludeDay })
          : point.date;
      } else {
        const ymd = point.date.length >= 10 ? point.date.slice(0, 10) : point.date;
        label = formatYmdBucketShortInZone(ymd, displayTimeZone, { includeYear });
      }
      return {
        date: point.date,
        label,
        drawdown: point.drawdown,
        drawdownPercent: drawdownPercentForPoint(point.drawdown, point.portfolio_value),
      };
    });
  }, [drawdownData, displayTimeZone]);

  const headerValues = useMemo(
    () => ({
      drawdown:
        maxDrawdown === null
          ? "N/A"
          : formatDisplayCurrency(maxDrawdown, displayCurrency, { locale: "en-US" }),
      drawdownPercent: formatPercentValue(maxDrawdownPercent),
    }),
    [displayCurrency, maxDrawdown, maxDrawdownPercent]
  );

  const seriesToggle = (
    <div className="flex w-full sm:w-auto">
      {(["drawdown", "drawdownPercent"] as const).map((key, index) => (
        <button
          key={key}
          type="button"
          data-active={activeSeries === key}
          className={cn(
            "flex min-w-[140px] flex-1 flex-col justify-center gap-1 border-t border-border px-[22px] py-4 text-left transition-colors sm:border-t-0",
            index > 0 && "sm:border-l",
            activeSeries === key && "bg-muted/50"
          )}
          style={{ fontFamily: STATS_FONT_FAMILY }}
          onClick={() => setActiveSeries(key)}
        >
          <span className="text-xs text-muted-foreground">{chartConfig[key].label}</span>
          {loading ? (
            <Skeleton height="28px" width="100px" mt={1} borderRadius="md" />
          ) : (
            <span
              className="text-lg font-semibold leading-none tabular-nums tracking-tight"
              style={{ color: DRAWDOWN_LINE_COLOR }}
            >
              {headerValues[key]}
            </span>
          )}
        </button>
      ))}
    </div>
  );

  const chartContent = (
    <ChartPanel
      loading={loading}
      error={error}
      errorAction={errorAction}
      errorColor={errorColor}
      chartData={chartData}
      activeSeries={activeSeries}
      axisTickColor={axisTickColor}
      displayCurrency={displayCurrency}
      displayTimeZone={displayTimeZone}
      fillHeight={fillHeight}
    />
  );



  if (compact) {
    return chartContent;
  }

  if (embedded) {
    return (
      <div
        className={cn(themeClass, fillHeight && "flex h-full min-h-0 flex-col")}
        style={{ fontFamily: STATS_FONT_FAMILY }}
      >
        <div className="flex flex-col border-b border-border sm:flex-row">{seriesToggle}</div>
        <div
          className={cn(
            statisticsShadcnCardContentClassName,
            "pt-4",
            fillHeight && "flex min-h-0 flex-1 flex-col"
          )}
        >
          <div
            className={cn(
              statisticsShadcnBarChartAreaClassName,
              fillHeight && "flex min-h-0 flex-1 flex-col"
            )}
          >
            {chartContent}
          </div>
        </div>
      </div>
    );
  }

  return (
    <Card
      data-statistics-shadcn-card
      className={cn(statisticsShadcnCardClassName, themeClass, fillHeight && "h-full min-h-0")}
      style={{ fontFamily: STATS_FONT_FAMILY }}
    >
      <CardHeader className="flex flex-col items-stretch border-b border-border !p-0 sm:flex-row">
        <div className="flex flex-1 flex-col justify-center gap-1 px-[22px] py-4 sm:py-5">
          <StatisticsCardHeadline>{title}</StatisticsCardHeadline>
          <CardDescription className="text-xs" style={{ fontFamily: STATS_FONT_FAMILY }}>
            {description}
          </CardDescription>
        </div>
        {seriesToggle}
      </CardHeader>
      <CardContent
        className={cn(
          statisticsShadcnCardContentClassName,
          "pt-4",
          fillHeight && "flex min-h-0 flex-1 flex-col"
        )}
      >
        <div
          className={cn(
            statisticsShadcnBarChartAreaClassName,
            fillHeight && "flex min-h-0 flex-1 flex-col"
          )}
        >
          {chartContent}
        </div>
      </CardContent>
    </Card>
  );
};

function ChartPanel({
  loading,
  error,
  errorAction = null,
  errorColor,
  chartData,
  activeSeries,
  axisTickColor,
  displayCurrency,
  displayTimeZone,
  fillHeight = false,
}: {
  loading: boolean;
  error: string | null;
  errorAction?: string | null;
  errorColor: string;
  chartData: DrawdownChartDatum[];
  activeSeries: DrawdownSeriesKey;
  axisTickColor: string;
  displayCurrency: string;
  displayTimeZone: string;
  fillHeight?: boolean;
}) {
  const { showWidgetError, widgetError, widgetAction } = useStatisticsWidgetErrorGate(error, errorAction);
  const chartId = useId().replace(/:/g, "");
  const fillDrawdown = `fillDrawdown-${chartId}`;
  const chartContainerClassName = fillHeight
    ? "aspect-auto h-full min-h-[300px] w-full min-w-0 [&_.recharts-surface]:overflow-visible [&_.recharts-wrapper]:!overflow-visible"
    : statisticsShadcnDailyActivityChartContainerClassName;
  const panelHeightProps = fillHeight
    ? { flex: "1" as const, minH: "300px", h: "100%", w: "100%" }
    : { h: "300px", w: "100%" };

  if (loading) {
    return (
      <Box {...panelHeightProps}>
        <Skeleton height="100%" borderRadius="md" />
      </Box>
    );
  }

  if (showWidgetError && widgetError) {
    return <StatisticsInlineError message={widgetError} action={widgetAction ?? undefined} />;
  }

  if (chartData.length === 0) {
    return (
      <Box
        {...panelHeightProps}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Text fontSize="sm" color="gray.500" fontFamily={STATS_FONT_FAMILY}>
          {STATISTICS_NO_DATA_TIMEFRAME_MESSAGE}
        </Text>
      </Box>
    );
  }

  const formatYAxis = (value: number) =>
    activeSeries === "drawdown"
      ? formatDisplayCurrency(value, displayCurrency, { locale: "en-US" })
      : `${value.toFixed(1)}%`;

  const formatTooltipValue = (value: number) =>
    activeSeries === "drawdown"
      ? formatDisplayCurrency(value, displayCurrency, { locale: "en-US" })
      : `${value.toFixed(2)}%`;

  return (
    <ChartContainer
      config={chartConfig}
      className={chartContainerClassName}
      style={{ fontFamily: STATS_FONT_FAMILY }}
    >
      <ComposedChart
        accessibilityLayer
        data={chartData}
        margin={{
          top: 16,
          right: 24,
          left: 16,
          bottom: 20,
        }}
      >
        <defs>
          <linearGradient id={fillDrawdown} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={DRAWDOWN_LINE_COLOR} stopOpacity={0.55} />
            <stop offset="95%" stopColor={DRAWDOWN_FILL_SOFT} stopOpacity={0.12} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} strokeDasharray="4 4" className="stroke-border/50" />
        <XAxis
          dataKey="label"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          minTickGap={32}
          interval="preserveStartEnd"
          tick={{ fill: axisTickColor, fontSize: 11 }}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          width={72}
          tick={{ fill: axisTickColor, fontSize: 11 }}
          tickFormatter={formatYAxis}
        />
        <ChartTooltip
          cursor={false}
          content={
            <ChartTooltipContent
              className={statsChartTooltipContentClassName("w-[180px]")}
              labelClassName={STATS_CHART_TOOLTIP_TITLE_CLASS}
              nameKey="views"
              labelFormatter={(_value, payload) => {
                const row = payload?.[0]?.payload as DrawdownChartDatum | undefined;
                if (!row?.date) {
                  return row?.label ?? "";
                }
                const parsed = new Date(row.date);
                if (Number.isNaN(parsed.getTime())) {
                  return row.label;
                }
                return formatTradeDateOnlyForDisplay(parsed, displayTimeZone);
              }}
              formatter={(value) => {
                const n = Number(value);
                return (
                  <TooltipRow
                    label={chartConfig[activeSeries].label}
                    value={formatTooltipValue(n)}
                  />
                );
              }}
            />
          }
        />
        <Area
          dataKey={activeSeries}
          type="monotone"
          fill={`url(#${fillDrawdown})`}
          stroke={DRAWDOWN_LINE_COLOR}
          strokeWidth={2}
          dot={false}
        />
      </ComposedChart>
    </ChartContainer>
  );
}

function TooltipRow({ label, value }: { label: string; value: string }) {
  return <StatisticsChartTooltipRow label={label} value={value} />;
}

export default DrawdownOverTimeChart;
